import { brand } from "../../config/theme";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUserStore } from "../../store/userStore";
import Swal from "sweetalert2";
import { LogoImage } from "../../components/base/LogoImage";
import { useUserCompanyName } from "../../hooks/useUserCompanyName";
import CompanySelector from "../../components/base/CompanySelector";
import type { Company } from "../../store/admin/companiesStore";

interface HeaderProps {
  onOpenAppsSidebar?: () => void;
  onToggleMobileMenu?: () => void;
  isMobileMenuOpen?: boolean;
  isDarkMode?: boolean;
  companies?: Company[];
  selectedCompany?: string | null;
  onCompanyChange?: (company: string | null) => void;
  isReseller?: boolean;
}

export default function TruckYardHeader({
  onOpenAppsSidebar,
  onToggleMobileMenu,
  isMobileMenuOpen,
  isDarkMode = false,
  companies = [],
  selectedCompany,
  onCompanyChange,
  isReseller = false,
}: HeaderProps) {
  const navigate = useNavigate();

  const { t } = useTranslation("common");
  const user = useUserStore((state) => state.user);
  const userRole = localStorage.getItem("userRole");
  const userCompanyName = useUserCompanyName();
  const isWebServicesRole = userRole === "web_services";

  const [showMultiApps, setShowMultiApps] = useState(false);
  const [currentApp, setCurrentApp] = useState("truck-yard");

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-bg" : "bg-[#f5f6f2]",
    border: isDarkMode ? "border-dark-border" : "border-[#e3e6de]",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-500",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
    cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-50",
    buttonText: isDarkMode ? "text-gray-400" : "text-gray-500",
    buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
    inputBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    inputBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    inputText: isDarkMode ? "text-white" : "text-gray-900",
    inputPlaceholder: isDarkMode
      ? "placeholder-gray-500"
      : "placeholder-gray-500",
  };

  const apps = [
    {
      id: "lite",
      name: t("apps.lite.name"),
      description: t("apps.lite.description"),
      icon: "ri-map-pin-line",
      color: "bg-primary-500",
      isActive: true,
    },
    {
      id: "admin",
      name: t("apps.admin.name"),
      description: t("apps.admin.description"),
      icon: "ri-dashboard-line",
      color: "bg-blue-500",
    },
    {
      id: "sg",
      name: "SG",
      description: t("header.resellerAdmin"),
      icon: "ri-shield-user-line",
      color: "bg-gray-600",
    },
    {
      id: "servicios",
      name: t("apps.servicios.name"),
      description: t("apps.servicios.description"),
      icon: "ri-truck-line",
      color: "bg-teal-500",
      isComingSoon: true,
    },
    {
      id: "facturacion",
      name: t("apps.facturacion.name"),
      description: t("apps.facturacion.description"),
      icon: "ri-bill-line",
      color: "bg-purple-500",
      isComingSoon: true,
    },
    {
      id: "reports-app",
      name: t("apps.reportes.name"),
      description: t("apps.reportes.description"),
      icon: "ri-file-chart-line",
      color: "bg-orange-500",
    },
    {
      id: "oasis",
      name: t("apps.oasis.name"),
      description: t("apps.oasis.description"),
      icon: "ri-building-line",
      color: "bg-[#2E5399]",
    },
    {
      id: "truck-yard",
      name: t("apps.truck-yard.name"),
      description: t("apps.truck-yard.description"),
      icon: "ri-truck-line",
      color: "bg-amber-500",
    },
    {
      id: "control",
      name: t("apps.torre-control.name"),
      description: t("apps.torre-control.description"),
      icon: "ri-radar-line",
      color: "bg-red-500",
      isLocked: true,
      isPremium: true,
    },
  ];

  const getRingColor = (appColor: string) => {
    const colorMap: { [key: string]: string } = {
      "bg-primary-500": "ring-primary-500",
      "bg-blue-500": "ring-blue-500",
      "bg-orange-500": "ring-orange-500",
      "bg-gray-600": "ring-gray-600",
      "bg-[#2E5399]": "ring-blue-600",
      "bg-red-500": "ring-red-500",
      "bg-teal-500": "ring-teal-500",
      "bg-purple-500": "ring-purple-500",
      "bg-amber-500": "ring-amber-500",
    };
    return colorMap[appColor] || "ring-primary-500";
  };

  const getCheckColor = (appColor: string) => {
    const colorMap: { [key: string]: string } = {
      "bg-primary-500": "bg-primary-500",
      "bg-blue-500": "bg-blue-500",
      "bg-orange-500": "bg-orange-500",
      "bg-gray-600": "bg-gray-600",
      "bg-[#2E5399]": "bg-blue-600",
      "bg-red-500": "bg-red-500",
      "bg-teal-500": "bg-teal-500",
      "bg-purple-500": "bg-purple-500",
      "bg-amber-500": "bg-amber-500",
    };
    return colorMap[appColor] || "bg-primary-500";
  };

  useEffect(() => {
    if (showMultiApps) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showMultiApps]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;

      if (!target.closest("[data-menu]")) {
        setShowMultiApps(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header
      className={`${themeClasses.bg} border-b ${themeClasses.border} px-4 py-2.5 shadow-sm`}
    >
      <div className="flex items-center justify-between">
        {/* Logo and Title */}
        <div className="flex items-center space-x-2 sm:space-x-3">
          {/* Mobile Menu Toggle */}
          {onToggleMobileMenu && (
            <button
              onClick={onToggleMobileMenu}
              className={`lg:hidden p-2 ${themeClasses.buttonHover} ${themeClasses.buttonText} rounded-lg transition-all`}
              aria-label="Toggle menu"
            >
              <i
                className={`${isMobileMenuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}
              ></i>
            </button>
          )}
          <LogoImage size="md" />
          <div className="hidden sm:block">
            <h1 className={`text-lg font-bold ${themeClasses.text}`}>
              {t("header.truckYard")}
            </h1>
            <p className={`text-xs ${themeClasses.textSecondary}`}>
              {t("header.dashboard")}
            </p>
          </div>
          {/* Company Selector (solo para reseller) */}
          {isReseller && (
            <div className="hidden lg:block">
              <CompanySelector
                value={selectedCompany ?? null}
                onChange={(id) => onCompanyChange?.(id)}
                isDarkMode={isDarkMode}
                isClearable
                companies={companies.length > 0 ? companies : undefined}
              />
            </div>
          )}
        </div>
        {/* Actions */}
        <div className="flex items-center space-x-2">
          {/* Profile - Opens Apps Sidebar */}
          <button
            onClick={onOpenAppsSidebar}
            className={`flex items-center space-x-2 p-1.5 ${themeClasses.buttonHover} rounded-lg transition-all`}
          >
            <div className="w-7 h-7 bg-gradient-to-br from-primary-500 to-primary-400 rounded-lg flex items-center justify-center shadow-sm">
              <span className="text-gray-900 text-xs font-semibold">
                {user?.first_name && user?.last_name
                  ? `${user.first_name.charAt(0).toUpperCase()}${user.last_name.charAt(0).toUpperCase()}`
                  : "DU"}
              </span>
            </div>
            <div className="text-left hidden sm:block">
              <p className={`text-sm font-medium ${themeClasses.text}`}>
                {user?.first_name && user?.last_name
                  ? `${user.first_name} ${user.last_name}`
                  : user?.email || t("general.user")}
              </p>
              <p
                className={`text-xs ${themeClasses.textSecondary} truncate max-w-[200px]`}
              >
                {userCompanyName
                  ? `${userCompanyName} · ${userRole || t("general.user")}`
                  : userRole || t("general.user")}
              </p>
            </div>
            <i
              className={`ri-arrow-down-s-line ${themeClasses.buttonText} text-sm`}
            ></i>
          </button>

          {/* Apps Sidebar panel */}
          {showMultiApps && !isWebServicesRole && (
            <div
              data-menu="multiApps"
              className={`fixed right-0 top-0 h-full w-96 ${themeClasses.bg} border-l ${themeClasses.border} z-40 shadow-2xl transform transition-transform duration-300`}
            >
              <div className="h-full flex flex-col">
                {/* Header */}
                <div
                  className={`p-6 border-b ${themeClasses.border} flex items-center justify-between`}
                >
                  <div>
                    <h2 className={`text-xl font-bold ${themeClasses.text}`}>
                      App Suite
                    </h2>
                    <p className={`text-sm ${themeClasses.textSecondary} mt-1`}>
                      {t("appsSidebar.subtitle")}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowMultiApps(false)}
                    className={`p-2 ${themeClasses.buttonText} ${themeClasses.buttonTextHover} ${themeClasses.buttonHover} rounded-lg transition-colors`}
                  >
                    <i className="ri-close-line text-lg"></i>
                  </button>
                </div>

                {/* Apps Grid */}
                <div className="flex-1 p-6">
                  <div className="grid grid-cols-3 gap-4">
                    {apps.map((app) => (
                      <div
                        key={app.id}
                        onClick={() => {
                          if (app.id === "lite") {
                            if (document.body.style.pointerEvents === "none")
                              document.body.style.pointerEvents = "none";
                            setShowMultiApps(false);
                            setCurrentApp("lite");
                            setTimeout(() => {
                              navigate("/map");
                              document.body.style.pointerEvents = "auto";
                            }, 100);
                            return;
                          } else if (app.id === "admin") {
                            if (document.body.style.pointerEvents === "none")
                              return;
                            document.body.style.pointerEvents = "none";
                            setShowMultiApps(false);
                            setCurrentApp("admin");
                            setTimeout(() => {
                              navigate("/admin/units");
                              document.body.style.pointerEvents = "auto";
                            }, 100);
                          } else if (app.id === "sg") {
                            if (document.body.style.pointerEvents === "none")
                              return;
                            document.body.style.pointerEvents = "none";
                            setShowMultiApps(false);
                            setTimeout(() => {
                              navigate("/sg/cuadre");
                              document.body.style.pointerEvents = "auto";
                            }, 100);
                          } else if (app.id === "oasis") {
                            if (document.body.style.pointerEvents === "none")
                              return;
                            document.body.style.pointerEvents = "none";
                            setShowMultiApps(false);
                            setCurrentApp("oasis");
                            setTimeout(() => {
                              navigate("/oasis/yards");
                              document.body.style.pointerEvents = "auto";
                            }, 100);
                          } else if (app.id === "reports-app") {
                            if (document.body.style.pointerEvents === "none")
                              return;
                            document.body.style.pointerEvents = "none";
                            setShowMultiApps(false);
                            setTimeout(() => {
                              navigate("/reports/history");
                              document.body.style.pointerEvents = "auto";
                            }, 100);
                          } else if (app.id === "truck-yard") {
                            if (document.body.style.pointerEvents === "none")
                              return;
                            document.body.style.pointerEvents = "none";
                            setShowMultiApps(false);
                            setCurrentApp("truck-yard");
                            setTimeout(() => {
                              navigate("/truck-yard");
                              document.body.style.pointerEvents = "auto";
                            }, 100);
                          } else if (app.isComingSoon) {
                            Swal.fire({
                              title: t("appsSidebar.comingSoon"),
                              text: t("appsSidebar.comingSoonText", {
                                name: app.name,
                              }),
                              icon: "info",
                              confirmButtonText: t("appsSidebar.understood"),
                              confirmButtonColor: brand.primary,
                            });
                          } else if (app.id === "control" && app.isLocked) {
                            // setShowControlTowerModal(true);
                          } else if (app.isLocked) {
                            alert(
                              `${t("appsSidebar.comingSoon")}: ${app.name}`,
                            );
                          } else {
                            alert(
                              `${t("appsSidebar.comingSoon")}: ${app.name}`,
                            );
                          }
                        }}
                        className={`relative group cursor-pointer p-3 rounded-xl transition-all duration-200 hover:scale-105 ${isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-50"
                          } ${currentApp === app.id
                            ? `ring-2 ${getRingColor(app.color)} ring-opacity-50`
                            : ""
                          }`}
                      >
                        {/* App Icon */}
                        <div
                          className={`w-12 h-12 ${app.color} rounded-xl flex items-center justify-center mb-2 mx-auto shadow-sm group-hover:shadow-md transition-shadow`}
                        >
                          <i className={`${app.icon} text-white text-lg`}></i>
                        </div>

                        {/* App Name */}
                        <h3
                          className={`text-xs font-medium ${themeClasses.text} text-center leading-tight`}
                        >
                          {app.name}
                        </h3>

                        {/* Status Indicators */}
                        {currentApp === app.id && (
                          <div
                            className={`absolute -top-1 -right-1 w-5 h-5 ${getCheckColor(app.color)} rounded-full flex items-center justify-center`}
                          >
                            <i className="ri-check-line text-white text-xs"></i>
                          </div>
                        )}

                        {app.isLocked && (
                          <div className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center">
                            <i className="ri-lock-line text-white text-xs"></i>
                          </div>
                        )}

                        {/* Premium Overlay */}
                        {app.isLocked && (
                          <div className="absolute inset-0 bg-black/10 rounded-xl flex items-end justify-center pb-1">
                            <span className="text-white text-xs font-medium bg-amber-500 px-1.5 py-0.5 rounded-full">
                              Pro
                            </span>
                          </div>
                        )}

                        {/* Hover Tooltip */}
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                          <div
                            className={`${themeClasses.bg} ${themeClasses.text} px-2 py-1 rounded-lg shadow-lg border ${themeClasses.border} whitespace-nowrap text-xs`}
                          >
                            <div className="font-medium">{app.name}</div>
                            <div
                              className={`text-xs ${themeClasses.textSecondary}`}
                            >
                              {app.description}
                            </div>
                          </div>
                          {/* Tooltip Arrow */}
                          <div
                            className={`absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-transparent ${isDarkMode
                              ? "border-t-[#2B2B2B]"
                              : "border-t-white"
                              }`}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Footer Info */}
                  <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <div className="text-center">
                      <p
                        className={`text-xs ${themeClasses.textSecondary} mb-2`}
                      >
                        {t("general.needMoreApps")}
                      </p>
                      <button className="text-xs text-primary-500 hover:text-primary-400 font-medium transition-colors">
                        {t("general.exploreStore")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
